import { z } from "zod";
const centsSchema = z.number().int().nonnegative().max(10_000_000);
const rateSchema = z.number().int().min(0).max(10_000);
const commercialTermsSchema = z
    .object({
    grossRetailCents: z.number().int().min(1).max(1_000_000),
    contractedRemittancePerSoldUnitCents: z.number().int().min(1).max(1_000_000),
    salesRepresentativeCommissionBasisPoints: rateSchema,
    usesEarlyPaymentDiscount: z.boolean(),
    earlyPaymentDiscountBasisPoints: rateSchema,
    paymentTermsDays: z.number().int().min(0).max(180),
    annualWorkingCapitalCostBasisPoints: z.number().int().min(1).max(10_000),
    customerReturnReserveBasisPoints: rateSchema,
    returnReserveReleaseDays: z.number().int().min(0).max(365),
    expectedReturnBasisPoints: rateSchema,
    expectedRefundBasisPoints: rateSchema,
    expectedChargebackBasisPoints: rateSchema,
    expectedFraudBasisPoints: rateSchema,
})
    .strict();
const fixedProgramCostsSchema = z
    .object({
    commerceHubOnboardingCents: centsSchema,
    platformSetupCents: centsSchema,
    qaAndTransitTestingCents: centsSchema,
    gs1AndGtinCents: centsSchema,
    packageSamplesCents: centsSchema,
    legalAndAccountingCents: centsSchema,
})
    .strict();
const perSoldCostsSchema = z
    .object({
    packageManufacturingCents: centsSchema,
    voucherOutboundDeliveryCents: centsSchema,
    pickPackAndWarehouseCents: centsSchema,
    platformPerOrderCents: centsSchema,
    paymentOrEdiPerOrderCents: centsSchema,
    baselineSupportLaborCents: centsSchema,
    lostVoucherReplacementBasisPoints: rateSchema,
    lostVoucherReplacementCents: centsSchema,
})
    .strict();
const perRedeemedCostsSchema = z
    .object({
    successfulGenerationCents: z.number().int().min(0).max(800),
    printManufacturingCents: centsSchema,
    bookOutboundShippingCents: centsSchema,
    taxAndDutiesCents: centsSchema,
    supportLaborCents: centsSchema,
    manualFulfillmentLaborCents: centsSchema,
    expectedFailedGenerationBasisPoints: rateSchema,
    failedGenerationAttemptCents: z.number().int().min(0).max(800),
    expectedAbandonedAfterSpendBasisPoints: rateSchema,
    abandonedGenerationAttemptCents: z.number().int().min(0).max(800),
    replacementBasisPoints: rateSchema,
    replacementBookAndShippingCents: centsSchema,
    completionIncentiveCents: centsSchema,
})
    .strict();
const reversalCostsSchema = z
    .object({
    returnProcessingCents: centsSchema,
    refundProcessingCents: centsSchema,
    chargebackProcessingCents: centsSchema,
    fraudLossCents: centsSchema,
})
    .strict();
export const adminQvcScenarioAssumptionsSchema = z
    .object({
    cohortUnits: z.number().int().min(2000).max(1000000).multipleOf(2000),
    commercialTerms: commercialTermsSchema,
    fixedProgramCosts: fixedProgramCostsSchema,
    perSoldCosts: perSoldCostsSchema,
    perRedeemedCosts: perRedeemedCostsSchema,
    reversalCosts: reversalCostsSchema,
    unredeemedLiabilityReserveBasisPoints: z.number().int().min(1).max(10000),
    redemptionBeforeRemittanceBasisPoints: rateSchema,
    launchGateRedemptionBasisPoints: z.union([z.literal(6000), z.literal(7000)]),
    approvedContributionFloorCentsPerSoldUnit: z.number().int().min(0).max(2000),
    approvedWorkingCapitalCents: z.number().int().min(1).max(100000000000),
})
    .strict();
export const adminQvcScenarioCommandSchema = z
    .object({
    modelRevision: z.literal("LC_QVC_SCENARIO_V1"),
    currency: z.literal("USD"),
    assumptions: adminQvcScenarioAssumptionsSchema,
})
    .strict()
    .superRefine((v, c) => {
    const t = v.assumptions.commercialTerms;
    if (t.contractedRemittancePerSoldUnitCents > t.grossRetailCents)
        c.addIssue({
            code: "custom",
            path: ["assumptions", "commercialTerms"],
            message: "Remittance cannot exceed the assumed retail price.",
        });
    if (t.expectedReturnBasisPoints +
        t.expectedRefundBasisPoints +
        t.expectedChargebackBasisPoints +
        t.expectedFraudBasisPoints >
        10000)
        c.addIssue({
            code: "custom",
            path: ["assumptions", "commercialTerms"],
            message: "Combined reversals cannot exceed 100%.",
        });
});
export const adminQvcScenarioResponseSchema = z
    .object({
    data: z
        .object({
        modelRevision: z.literal("LC_QVC_SCENARIO_V1"),
        currency: z.literal("USD"),
        basis: z.literal("OWNER_ENTERED_ASSUMPTIONS_NOT_COMMERCIAL_TERMS"),
        launchAssessment: z.literal("NOT_ASSESSED"),
        exclusions: z.array(z.string()).max(20),
        calculation: z
            .object({
            commercialSummary: z.record(z.string(), z.number().finite()),
            scenarios: z
                .array(z
                .object({
                redemptionBasisPoints: z.number().int(),
                redeemedUnits: z.number(),
                unredeemedUnits: z.number(),
                netRevenueCents: z.number(),
                redeemedCostsCents: z.number(),
                unredeemedLiabilityReserveCents: z.number(),
                totalCostsAndReservesCents: z.number(),
                totalContributionCents: z.number(),
                contributionPerSoldUnitCents: z.number(),
                launchFloorApplies: z.boolean(),
                contributionFloorPassed: z.boolean(),
                cashRequiredBeforeRemittanceCents: z.number(),
                returnReserveHeldCents: z.number(),
                financingCostCents: z.number(),
                workingCapitalRequirementCents: z.number(),
                workingCapitalPassed: z.boolean(),
            })
                .strict())
                .length(5),
        })
            .strict(),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-qvc-scenario.js.map